import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexGrid, ApexStroke, ApexTitleSubtitle, ApexXAxis } from 'ng-apexcharts';
import { ToastrService } from 'ngx-toastr';
import { AppComponent } from 'src/app/app.component';
import { NodeConfigurationDTO } from 'src/app/model/configuration/node-configuration-dto';
import { CoreStatusDTO } from 'src/app/model/core-status-dto';
import { CoreStatus } from 'src/app/model/nodes/status/core-status';
import { NodeService } from 'src/app/services/node.service';
import { NodeLifecycleParserModalComponent } from './node-lifecycle-parser-modal/node-lifecycle-parser-modal.component';
import { DiagnosisDataDTO } from 'src/app/model/diagnosis/diagnosis-data-dto';
import { NodeLifecycleDetectorModalComponent } from './node-lifecycle-detector-modal/node-lifecycle-detector-modal.component';
import ApexCharts from 'apexcharts'
import cloneDeep from 'lodash.clonedeep';
import { NodeDiagnosisComponent } from '../node-manager/node-diagnosis/node-diagnosis.component';
import { Location } from '@angular/common';

export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    dataLabels: ApexDataLabels;
    grid: ApexGrid;
    stroke: ApexStroke;
    title: ApexTitleSubtitle;
    annotations: ApexAnnotations | undefined;
};

export type ParserLearningConfig = {
    timeLimitHours: number,
    timeLimitHoursWithoutFeatures: number,
    variables: { [dataSource: string]: string[] };
}

export type DetectorLearningConfig = {
    timeLimitHours?: number,
    uclVariationPercentage?: number
}

@Component({
    selector: 'app-node-lifecycle',
    templateUrl: './node-lifecycle.component.html',
    styleUrl: './node-lifecycle.component.scss'
})
export class NodeLifecycleComponent implements AfterViewInit, OnDestroy {
    public id: number = -1
    public nodeGuid: string = ''
    public nodeConfiguration: NodeConfigurationDTO | undefined
    public nodeStatus: CoreStatusDTO | undefined
    public executingAction: boolean = false
    public selectedFeature: string | undefined
    public tabParser = 'parser'
    public tabDetector = 'detector'
    public tabDiagnosis = 'diagnosis'
    public currentTab: string = ''
    public previousStatus: CoreStatus | undefined
    public cronjob: any

    @ViewChild("nodeDiagnosisView") nodeDiagnosisView: NodeDiagnosisComponent | undefined
    public diagnosisData: DiagnosisDataDTO[] = []
    public totalGeneratedFeaturesEvoChart: ApexCharts | undefined
    public netflowEvolutionChart: ApexCharts | undefined
    public syslogEvolutionChart: ApexCharts | undefined
    public snmpEvolutionChart: ApexCharts | undefined
    public ucldEvolutionChart: ApexCharts | undefined
    public uclqEvolutionChart: ApexCharts | undefined
    public ucldVariationEvolutionChart: ApexCharts | undefined
    public uclqVariationEvolutionChart: ApexCharts | undefined

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private location: Location,
        public nodeService: NodeService,
        public dialog: MatDialog,
        public fb: FormBuilder,
        private readonly toastr: ToastrService
    ) {
        this.id = isNaN(+(this.route.snapshot.paramMap.get('id') ?? -1)) ? -1 : +(this.route.snapshot.paramMap.get('id') ?? -1)
        const viewText = this.id > -1 ? 'life cycle: ' + this.id : 'life cycle'

        this.nodeGuid = this.route.snapshot.paramMap.get('guid') ?? ''
        AppComponent.updateAppHeader(viewText, 'node manager', '/node-manager')
    }

    ngAfterViewInit(): void {
        this.loadNodeConfiguration()
        this.cronjob = setInterval(() => { this.loadNodeConfiguration() }, 10000)
    }

    ngOnDestroy(): void {
        clearInterval(this.cronjob)
    }

    loadNodeConfiguration(): void {
        this.nodeService.getNodeConfiguration(this.id)
            .then(dto => {
                this.nodeConfiguration = dto

                this.nodeService.getStatus(this.id)
                    .then(status => {
                        this.nodeStatus = status
                        if (this.previousStatus !== this.nodeStatus?.status) {
                            this.resetTabSelection()
                        }
                        setTimeout(() => {
                            this.buildCharts()
                        }, 500);
                        this.previousStatus = this.nodeStatus.status
                    }).catch(_ => {
                        this.toastr.error('Error getting node status.')
                    })
            }).catch(_ => {
                this.toastr.error('Node data is not valid or cannot establish a connection.')
                this.location.back()
            })
    }

    resetTabSelection(): void {
        if (!!this.nodeStatus?.parserLearning && !this.nodeStatus?.detectorLearning) {
            this.currentTab = this.tabParser
        }

        if (this.nodeStatus?.detectorLearning) {
            this.currentTab = this.tabDetector
        }

        if (this.nodeStatusOperating()) {
            this.selectDiagnosisTab()
        }

        if (this.nodeStatusInactive()) {
            this.currentTab = ''
        }
    }

    selectDiagnosisTab(): void {
        this.currentTab = this.tabDiagnosis
        this.nodeDiagnosisView?.getObservations()
    }

    parserTabActive(): boolean {
        return !!this.nodeStatus?.parserLearning && this.currentTab === this.tabParser
    }

    detectorTabActive(): boolean {
        return !!this.nodeStatus?.detectorLearning && this.currentTab === this.tabDetector
    }

    diagnosisTabEnabled(): boolean {
        return !!this.nodeStatus && !this.nodeStatusInactive()
    }

    hasLearningData(): boolean {
        return !!this.nodeStatus?.detectorLearning || !!this.nodeStatus?.parserLearning
    }

    modifyNodeFeatures(): void {
        this.router.navigate(['/node-manager/configure/', this.id])
    }

    callbackAfterLifecycleAction(statusPromise: Promise<CoreStatusDTO>) {
        this.executingAction = true
        statusPromise.then(status => {
            this.nodeStatus = status
            this.currentTab = ''
            this.resetTabSelection()
            setTimeout(() => {
                this.buildCharts()
            }, 700);
        })
            .catch(_ => {
                this.toastr.error('Error executing action.')
            })
            .finally(() => {
                this.executingAction = false
            })
    }

    startWorking(): void {
        this.callbackAfterLifecycleAction(this.nodeService.startWorking(this.id))
    }

    stopWorking(): void {
        this.callbackAfterLifecycleAction(this.nodeService.stopWorking(this.id))
    }

    startParserLearning(): void {
        this.dialog.open(NodeLifecycleParserModalComponent, {
            data: this.nodeConfiguration,
        }).afterClosed().subscribe((selectedConfiguration: ParserLearningConfig) => {
            if (selectedConfiguration !== undefined) {
                this.callbackAfterLifecycleAction(
                    this.nodeService.startParserLearning(
                        this.id,
                        selectedConfiguration.timeLimitHours,
                        selectedConfiguration.timeLimitHoursWithoutFeatures,
                        selectedConfiguration.variables)
                )
            }
        })
    }

    skipParserLearning(): void {
        this.callbackAfterLifecycleAction(
            this.nodeService.skipParserLearning(this.id)
        )
    }

    startDetectorLearning(): void {
        this.dialog.open(NodeLifecycleDetectorModalComponent)
            .afterClosed()
            .subscribe((selectedConfiguration: DetectorLearningConfig) => {
                if (selectedConfiguration !== undefined) {
                    this.callbackAfterLifecycleAction(
                        this.nodeService.startDetectorLearning(this.id, selectedConfiguration.timeLimitHours!, selectedConfiguration.uclVariationPercentage!)
                    )
                }
            })
    }

    finishWork(): void {
        this.callbackAfterLifecycleAction(this.nodeService.finish(this.id))
    }

    resetWork(): void {
        this.callbackAfterLifecycleAction(this.nodeService.reset(this.id))
    }

    cancelNodeCurrentWork(): void {
        this.executingAction = true
        this.callbackAfterLifecycleAction(this.nodeService.cancel(this.id))
    }

    nodeStatusInactive(): boolean {
        return !!this.nodeStatus?.status && this.nodeStatus.status.toString() === CoreStatus[CoreStatus.Inactive]
    }

    nodeStatusParserLearning(): boolean {
        return !!this.nodeStatus?.status && this.nodeStatus.status.toString() === CoreStatus[CoreStatus.ParserLearning]
    }

    nodeStatusParserLearningFinished(): boolean {
        return !!this.nodeStatus?.status && this.nodeStatus.status.toString() === CoreStatus[CoreStatus.ParserLearningFinished]
    }

    nodeStatusDetectorLearning(): boolean {
        return !!this.nodeStatus?.status && this.nodeStatus.status.toString() === CoreStatus[CoreStatus.DetectorLearning]
    }

    nodeStatusDetectorLearningFinished(): boolean {
        return !!this.nodeStatus?.status && this.nodeStatus.status.toString() === CoreStatus[CoreStatus.DetectorLearningFinished]
    }

    nodeStatusOperating(): boolean {
        return !!this.nodeStatus?.status && this.nodeStatus.status.toString() === CoreStatus[CoreStatus.Working]
    }

    buildCharts(): void {
        if (this.nodeStatus?.status.toString() === CoreStatus[CoreStatus.DetectorLearning] && this.nodeStatus?.detectorLearning) {
            this.buildDetectorCharts()
        }

        if (this.nodeStatus?.status.toString() === CoreStatus[CoreStatus.ParserLearning] && this.nodeStatus?.parserLearning) {
            this.buildParserTotalGeneratedFeaturesChart()
            this.buildParserGeneratedFeaturesChart()
        }
    }

    buildParserTotalGeneratedFeaturesChart(): void {
        const parserTotalGeneratedFeatures = this.nodeStatus!.parserLearning!.totalGeneratedFeaturesEvolution.map((key) => ({ x: key.timestamp, y: key.value }))

        let parserTotalGeneratedFeaturesOptions = {
            series: [
                {
                    name: "Total generated features",
                    data: parserTotalGeneratedFeatures
                }
            ],
            chart: {
                height: 250,
                type: "line",
                zoom: {
                    enabled: false
                },
                fontFamily: 'monospace'
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: "straight"
            },
            title: {
                text: "Total generated features",
                align: "left"
            },
            grid: {
                row: {
                    colors: ["#f3f3f3", "transparent"], // takes an array which will be repeated on columns
                    opacity: 0.5
                }
            },
            xaxis: {
                type: 'category',
                labels: {
                    show: false,
                }
            },
            yaxis: {
                labels: {
                    formatter: this.evolutionFormatter
                }
            }
        }

        if (this.totalGeneratedFeaturesEvoChart === undefined) {
            this.totalGeneratedFeaturesEvoChart = this.createApexCharts('#totalGeneratedFeaturesEvoChart', parserTotalGeneratedFeaturesOptions as any)
            this.totalGeneratedFeaturesEvoChart.render()
        } else {
            this.totalGeneratedFeaturesEvoChart.updateSeries(parserTotalGeneratedFeaturesOptions.series)
        }
    }

    buildParserGeneratedFeaturesChart(): void {
        const netflowParserGeneratedFeatures = this.nodeStatus!.parserLearning!.generatedFeaturesEvolution['netflow'].map((key) => ({ x: key.timestamp, y: key.value }))
        let parserGeneratedFeaturesOptions = {
            series: [
                {
                    name: `NETFLOW feature evolution`,
                    data: netflowParserGeneratedFeatures
                }
            ],
            chart: {
                height: 250,
                type: "line",
                zoom: {
                    enabled: false
                },
                fontFamily: 'monospace'
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: "straight"
            },
            title: {
                text: `NETFLOW feature evolution`,
                align: "left"
            },
            grid: {
                row: {
                    colors: ["#f3f3f3", "transparent"], // takes an array which will be repeated on columns
                    opacity: 0.5
                }
            },
            xaxis: {
                type: 'category',
                labels: {
                    show: false,
                }
            },
            yaxis: {
                labels: {
                    formatter: this.evolutionFormatter
                }
            }
        }
        if (this.netflowEvolutionChart === undefined) {
            this.netflowEvolutionChart = this.createApexCharts('#netflowEvolutionChart', parserGeneratedFeaturesOptions as any)
            this.netflowEvolutionChart.render()
        } else {
            this.netflowEvolutionChart.updateSeries(parserGeneratedFeaturesOptions.series)
        }

        const syslogParserGeneratedFeatures = this.nodeStatus!.parserLearning!.generatedFeaturesEvolution['syslog'].map((key) => ({ x: key.timestamp, y: key.value }))
        parserGeneratedFeaturesOptions.series[0].data = syslogParserGeneratedFeatures
        parserGeneratedFeaturesOptions.series[0].name = 'SYSLOG feature evolution'
        if (this.syslogEvolutionChart === undefined) {
            parserGeneratedFeaturesOptions.title.text = 'SYSLOG feature evolution'
            this.syslogEvolutionChart = this.createApexCharts('#syslogEvolutionChart', parserGeneratedFeaturesOptions as any)
            this.syslogEvolutionChart.render()
        } else {
            this.syslogEvolutionChart.updateSeries(parserGeneratedFeaturesOptions.series)
        }

        const snmpParserGeneratedFeatures = this.nodeStatus!.parserLearning!.generatedFeaturesEvolution['snmp'].map((key) => ({ x: key.timestamp, y: key.value }))
        parserGeneratedFeaturesOptions.series[0].data = snmpParserGeneratedFeatures
        parserGeneratedFeaturesOptions.series[0].name = 'SNMP feature evolution'

        if (this.snmpEvolutionChart === undefined) {
            parserGeneratedFeaturesOptions.title.text = 'SNMP feature evolution'
            this.snmpEvolutionChart = this.createApexCharts('#snmpEvolutionChart', parserGeneratedFeaturesOptions as any)
            this.snmpEvolutionChart.render()
        } else {
            this.snmpEvolutionChart.updateSeries(parserGeneratedFeaturesOptions.series)
        }
    }

    buildDetectorCharts(): void {
        const detectorUCLdEvolution = this.nodeStatus!.detectorLearning!.ucLdEvolution.map((key) => ({ x: key.timestamp, y: key.value }))
        const detectorUCLqEvolution = this.nodeStatus!.detectorLearning!.ucLqEvolution.map((key) => ({ x: key.timestamp, y: key.value }))
        const detectorUCLdVariationEvolution = this.nodeStatus!.detectorLearning!.ucLdVariationEvolution.map((key) => ({ x: key.timestamp, y: +(key.value * 100).toFixed(1) }))
        const detectorUCLqVariationEvolution = this.nodeStatus!.detectorLearning!.ucLqVariationEvolution.map((key) => ({ x: key.timestamp, y: +(key.value * 100).toFixed(1) }))

        let annotation = {
            yaxis: [
                {
                    y: (this.nodeStatus?.detectorLearning?.uclVariationPercentage ?? 0) * 100,
                    borderColor: '#FF0000',
                    label: {
                        text: `UCL variation limit: ${(this.nodeStatus?.detectorLearning?.uclVariationPercentage ?? 0) * 100}%`,
                        style: {
                            color: '#fff',
                            background: '#FF0000'
                        }
                    }
                }
            ]
        }

        let basicChartOptions: ChartOptions = {
            series: [
                {
                    name: "Chart",
                    data: detectorUCLdEvolution
                }
            ],
            chart: {
                height: 250,
                type: "line",
                zoom: {
                    enabled: false
                },
                fontFamily: 'monospace'
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: "straight"
            },
            title: {
                text: "Chart",
                align: "left"
            },
            grid: {
                row: {
                    colors: ["#f3f3f3", "transparent"], // takes an array which will be repeated on columns
                    opacity: 0.5
                }
            },
            xaxis: {
                type: 'category',
                labels: {
                    show: false
                }
            },
            yaxis: {
                labels: {
                    formatter: this.evolutionFormatter
                }
            },
            annotations: annotation
        }

        let ucldEvolutionChartOptions = cloneDeep(basicChartOptions)
        ucldEvolutionChartOptions.series = [
            {
                name: "UCLd Evolution",
                data: detectorUCLdEvolution
            }
        ]
        ucldEvolutionChartOptions.title.text = "UCLd Evolution"
        ucldEvolutionChartOptions.annotations = undefined

        let uclqEvolutionChartOptions = cloneDeep(basicChartOptions)
        uclqEvolutionChartOptions.series = [
            {
                name: "UCLq Evolution",
                data: detectorUCLqEvolution
            }
        ]
        uclqEvolutionChartOptions.title.text = "UCLq Evolution"
        uclqEvolutionChartOptions.annotations = undefined

        let ucldVariationEvolutionChartOptions = cloneDeep(basicChartOptions)
        ucldVariationEvolutionChartOptions.series = [
            {
                name: "UCLd Variation Evolution",
                data: detectorUCLdVariationEvolution
            }
        ]
        ucldVariationEvolutionChartOptions.title.text = "UCLd Variation Evolution"
        ucldVariationEvolutionChartOptions.yaxis.labels!.formatter = this.variationFormatter

        let uclqVariationEvolutionChartOptions = cloneDeep(basicChartOptions)
        uclqVariationEvolutionChartOptions.series = [
            {
                name: "UCLq Variation Evolution",
                data: detectorUCLqVariationEvolution
            }
        ]
        uclqVariationEvolutionChartOptions.title.text = "UCLq Variation Evolution"
        uclqVariationEvolutionChartOptions.yaxis.labels!.formatter = this.variationFormatter

        if (this.ucldEvolutionChart === undefined) {
            this.ucldEvolutionChart = this.createApexCharts("#ucldEvolutionChart", ucldEvolutionChartOptions)
            this.ucldEvolutionChart.render()
        } else {
            this.ucldEvolutionChart.updateSeries(ucldEvolutionChartOptions.series)
        }

        if (this.uclqEvolutionChart === undefined) {
            this.uclqEvolutionChart = this.createApexCharts("#uclqEvolutionChart", uclqEvolutionChartOptions)
            this.uclqEvolutionChart.render()
        } else {
            this.uclqEvolutionChart.updateSeries(uclqEvolutionChartOptions.series)
        }

        if (this.ucldVariationEvolutionChart === undefined) {
            this.ucldVariationEvolutionChart = this.createApexCharts("#ucldVariationEvolutionChart", ucldVariationEvolutionChartOptions)
            this.ucldVariationEvolutionChart.render()
        } else {
            this.ucldVariationEvolutionChart.updateSeries(ucldVariationEvolutionChartOptions.series)
        }

        if (this.uclqVariationEvolutionChart === undefined) {
            this.uclqVariationEvolutionChart = this.createApexCharts("#uclqVariationEvolutionChart", uclqVariationEvolutionChartOptions)
            this.uclqVariationEvolutionChart.render()
        } else {
            this.uclqVariationEvolutionChart.updateSeries(uclqVariationEvolutionChartOptions.series)
        }
    }

    createApexCharts(selector: string, chartOptions: ChartOptions) {
        return new ApexCharts(document.querySelector(selector), chartOptions)
    }

    evolutionFormatter(val: number): string | string[] {
        if (val !== undefined) {
            return (val / 2 * 2).toString()
        }

        return '0'
    }

    variationFormatter(val: number): string | string[] {
        if (val !== undefined) {
            return (val / 2 * 2).toString() + "%"
        }

        return '0'
    }
}
